import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { api } from '../../lib/api';
import { Loader2, TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';

const PERIODS = [
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
];

const Stat = ({ label, value, hint, color = 'text-tp-text', testId }) => (
  <Card className="p-4 border-tp-border" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-tp-text-light mb-1">{label}</p>
    <p className={`text-3xl font-bold ${color}`}>{value}</p>
    {hint && <p className="text-xs text-tp-text-light mt-1">{hint}</p>}
  </Card>
);

const Ratio = ({ label, value, threshold = 30 }) => {
  const num = typeof value === 'number' ? value : null;
  const isHealthy = num === null ? null : num >= threshold;
  const Icon = isHealthy ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center justify-between text-sm py-1.5 px-2 border-b border-tp-border last:border-0">
      <span className="text-tp-text">{label}</span>
      <span className={`font-semibold flex items-center gap-1 ${
        num === null ? 'text-tp-text-light' : isHealthy ? 'text-green-600' : 'text-orange-600'
      }`}>
        {num !== null && <Icon className="h-3.5 w-3.5" />}
        {num !== null ? `${num}%` : '—'}
      </span>
    </div>
  );
};

const ConversionDashboardPage = () => {
  const [period, setPeriod] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/conversion?days=${period}`);
      if (res.ok) {
        setData(await res.json());
      } else {
        setError('Erreur de chargement des données.');
      }
    } catch (e) {
      setError('Connexion impossible au serveur.');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="conversion-loading">
        <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const m = data?.metrics || {};
  const r = data?.ratios_pct || {};
  const noise = data?.noise || {};
  const top = data?.top_admitted_categories || [];

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="conversion-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display text-tp-primary">Conversion — 5 chiffres</h1>
          <p className="text-sm text-tp-text-light mt-1">
            Funnel candidat sur les {period} derniers jours. Mis à jour en temps réel.
          </p>
        </div>
        <div className="flex gap-2" data-testid="period-toggles">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                period === p.value
                  ? 'bg-tp-primary text-white border-tp-primary'
                  : 'border-tp-border text-tp-text hover:bg-tp-bg'
              }`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* 5 chiffres principaux */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat
          label="Visiteurs landing"
          value={m.landing_view ?? 0}
          hint="Vues uniques"
          testId="stat-landing-view"
        />
        <Stat
          label="Formulaires commencés"
          value={m.form_started ?? 0}
          color="text-tp-primary"
          testId="stat-form-started"
        />
        <Stat
          label="Soumis"
          value={m.form_submitted ?? 0}
          color="text-tp-primary"
          testId="stat-form-submitted"
        />
        <Stat
          label="Admis"
          value={m.candidate_admitted ?? 0}
          hint="Passé tous les filtres auto"
          color="text-green-600"
          testId="stat-admitted"
        />
        <Stat
          label="Présentables"
          value={m.candidate_presentable ?? 0}
          hint="Validés par admin"
          color="text-green-700"
          testId="stat-presentable"
        />
      </div>

      {/* Ratios entre étapes */}
      <Card className="p-5 border-tp-border" data-testid="ratios-card">
        <h2 className="text-lg font-semibold text-tp-text mb-3">Taux de conversion entre étapes</h2>
        <Ratio label="Landing → Formulaire commencé" value={r.landing_to_started} threshold={30} />
        <Ratio label="Commencé → Soumis" value={r.started_to_submitted} threshold={40} />
        <Ratio label="Soumis → Admis (auto)" value={r.submitted_to_admitted} threshold={60} />
        <Ratio label="Admis → Présentable" value={r.admitted_to_presentable} threshold={20} />
        <Ratio label="🔥 Landing → Présentable (global)" value={r.landing_to_presentable} threshold={1} />
      </Card>

      {/* Bruit (rejets auto + doublons) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-4 border-tp-border bg-orange-50/30" data-testid="noise-rejected">
          <p className="text-xs uppercase tracking-wider text-tp-text-light">Auto-rejetés</p>
          <p className="text-2xl font-bold text-orange-700">{noise.auto_rejected ?? 0}</p>
          <p className="text-xs text-tp-text-light mt-1">non-remote, pas d'ordi/internet, etc.</p>
        </Card>
        <Card className="p-4 border-tp-border bg-orange-50/30" data-testid="noise-duplicate">
          <p className="text-xs uppercase tracking-wider text-tp-text-light">Doublons bloqués</p>
          <p className="text-2xl font-bold text-orange-700">{noise.duplicate_blocked ?? 0}</p>
          <p className="text-xs text-tp-text-light mt-1">re-soumissions sous 7 jours</p>
        </Card>
      </div>

      {/* Top catégories */}
      {top.length > 0 && (
        <Card className="p-5 border-tp-border" data-testid="top-categories">
          <h2 className="text-lg font-semibold text-tp-text mb-3">
            Top catégories (admis) sur la période
          </h2>
          <div className="space-y-1">
            {top.map((c, i) => (
              <div key={c.category} className="flex items-center justify-between py-1.5 text-sm border-b border-tp-border last:border-0">
                <span className="text-tp-text">{i + 1}. {c.category}</span>
                <span className="font-semibold text-tp-primary">{c.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!m.landing_view && !m.form_submitted && (
        <Card className="p-5 border-orange-300 bg-orange-50/30" data-testid="empty-state">
          <p className="text-sm text-tp-text">
            <strong>Aucune donnée pour cette période.</strong>{' '}
            Les premiers visiteurs commenceront à apparaître dès qu'ils ouvriront la landing.
          </p>
        </Card>
      )}
    </div>
  );
};

export default ConversionDashboardPage;
